import React, { createContext } from 'react';
import { useAppSelector } from '../../../app/hooks';
import useScanDataTableExists from '../../duckDB/hooks/useScanDataTableExists';
import { ImageInfoProps } from '../../imageViewer/ImageInfoSlot';
import { selectCurrMeasure } from '../../select/selectSlice';
import { useBlockAverage } from '../hooks/useBlockAverage';
import {
  BlueberryData,
  BlueberryFilterEnum,
  useBlueberryQuery,
} from '../hooks/useBlueberryQuery';
import { useColumnsExistInDB } from '../hooks/useColumnsExistInDB';
import useFilterScanData, {
  ScanDataFilterEnum,
} from '../hooks/useFilterScanData';
import useGetScanDataAndUpdateDuckDB from '../hooks/useGetScanDataAndUpdateDuckDB';
import useIsBlueberryFeature from '../hooks/useIsBlueberryFeature';
import {
  MapPointsQueryResult,
  useMapPointsScanData,
} from '../hooks/useMapPointsScanData';
import {
  ColorDistributionQueryResult,
  useScanDataColorDistribution,
} from '../hooks/useScanDataColorDistribution';
import {
  DistributionQueryResult,
  useScanDataDistribution,
} from '../hooks/useScanDataDistribution';
import useScanDataInfoSlots from '../hooks/useScanDataInfoSlots';
import { useScanDataStdDev } from '../hooks/useScanDataStdDev';
import {
  convertMeasureCubeRanchToScanDataBlock,
  ScanData,
  ScanDataEnum,
} from '../scanDataDefinitions';

export interface DataItem<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
}

export interface ScanDataState {
  scanData: DataItem<ScanData[]>;
  currPointScanData: DataItem<ScanData[]>;
  currHoverPointScanData: DataItem<ScanData[]>;
  standardDeviation: DataItem<number>;
  mean: ReturnType<typeof useBlockAverage>;
  distributionInterval: DataItem<DistributionQueryResult[]>;
  infoSlots: DataItem<ImageInfoProps[]>;
  colorsDistribution: DataItem<ColorDistributionQueryResult[]>;
  mapPoints: DataItem<MapPointsQueryResult[]>;
  blueberryData: DataItem<BlueberryData[]>;
  blueberryHoverPointData: DataItem<BlueberryData[]>;
}

interface ScanDataProviderProps {
  children: React.ReactNode;
}

// Create the context
export const ScanDataContext = createContext<ScanDataState | undefined>(
  undefined
);

const SCAN_DATA_TABLE_NAME = 'scanData';

// Create the provider
export const ScanDataProvider: React.FC<ScanDataProviderProps> = ({
  children,
}) => {
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currScanDataMeasure =
    convertMeasureCubeRanchToScanDataBlock(currMeasure);
  const isBlueberry = useIsBlueberryFeature();

  // Fetch the scan data and load it into DuckDB
  useGetScanDataAndUpdateDuckDB();
  // Check if scan data table exists and update redux store
  useScanDataTableExists(SCAN_DATA_TABLE_NAME);
  // Check which columns are available in scan data table and update redux store
  useColumnsExistInDB(SCAN_DATA_TABLE_NAME, Object.values(ScanDataEnum));

  // Update the state with fetched data
  const scanDataResponse = useFilterScanData({
    skip: isBlueberry,
  });

  const currPointScanDataResponse = useFilterScanData({
    activeFilters: [ScanDataFilterEnum.point_id],
    // skip: isBlueberry,
  });

  const currHoverPointScanDataResponse = useFilterScanData({
    activeFilters: [ScanDataFilterEnum.point_id], // filter by point id
    isMapHoverData: true,
    skip: isBlueberry,
  });

  const stdDevResponse = useScanDataStdDev({
    measure_col: currScanDataMeasure?.toString() || null,
    skip: isBlueberry,
  });

  const mean = useBlockAverage({
    skip: isBlueberry,
  });

  const distributionResponse = useScanDataDistribution({
    mean: mean.data,
    skip: isBlueberry,
  });

  const infoSlotsResponse = useScanDataInfoSlots(mean);

  const colorsDistribution = useScanDataColorDistribution({
    skip: isBlueberry,
  });

  // Disabled for performance.  It's been running for months without any issues so seems like our distribution are good.
  // useValidateDistributionResults(distributionResponse, colorsDistribution);

  const mapPoints = useMapPointsScanData({
    activeFilters: [],
  });

  const blueberryData = useBlueberryQuery({});
  const blueberryHoverPointData = useBlueberryQuery({
    activeFilters: [BlueberryFilterEnum.point_id],
    isMapHoverData: true,
  });

  const value = {
    scanData: scanDataResponse,
    currPointScanData: currPointScanDataResponse,
    currHoverPointScanData: currHoverPointScanDataResponse,
    standardDeviation: stdDevResponse,
    mean,
    distributionInterval: distributionResponse,
    infoSlots: infoSlotsResponse,
    colorsDistribution,
    mapPoints,
    blueberryData,
    blueberryHoverPointData,
  };

  return (
    <ScanDataContext.Provider value={value}>
      {children}
    </ScanDataContext.Provider>
  );
};
